import * as React from 'react';
import * as Sentry from '@sentry/react';

import {
  ErrorContext,
  ObservabilityConfig,
  ObservabilityUser,
  reportError,
  resolveObservabilityConfig,
  sanitize,
  setErrorReporter,
  setObservabilityUser,
  setObservabilityUserSetter
} from './core';

export interface BrowserObservabilityConfig extends ObservabilityConfig {
  tracePropagationTargets?: Array<string | RegExp>;
  replayOptions?: Record<string, unknown>;
  feedback?: boolean;
  feedbackOptions?: Record<string, unknown>;
  user?: ObservabilityUser | null;
}

export interface BrowserLogger {
  debug(event: string, payload?: unknown): void;
  info(event: string, payload?: unknown): void;
  warn(event: string, payload?: unknown): void;
  error(event: string, payload?: unknown): string | null;
}

export interface ErrorBoundaryProps {
  children?: React.ReactNode;
  fallback?: React.ReactNode | ((props: ErrorBoundaryFallbackProps) => React.ReactNode);
  section?: string;
  showDialog?: boolean;
  beforeCapture?: (...args: unknown[]) => void;
  onError?: (...args: unknown[]) => void;
}

export interface ErrorBoundaryFallbackProps {
  error: Error;
  componentStack: string;
  resetError: () => void;
}

interface ErrorBoundaryState {
  error: Error | null;
  componentStack: string;
}

export function initReactObservability(config: BrowserObservabilityConfig = {}) {
  const resolved = resolveObservabilityConfig(config);

  setObservabilityUserSetter((user) => {
    Sentry.setUser(user);
  });

  setErrorReporter((error, context) => {
    if (!resolved.enabled || !resolved.dsn) {
      return null;
    }

    return Sentry.captureException(error, {
      extra: sanitize(context)
    });
  });

  if (config.user !== undefined) {
    setObservabilityUser(config.user);
  }

  if (!resolved.enabled || !resolved.dsn) {
    return resolved;
  }

  Sentry.init({
    dsn: resolved.dsn,
    enabled: resolved.enabled,
    environment: resolved.environment,
    release: resolved.release,
    sendDefaultPii: resolved.sendDefaultPii,
    tracesSampleRate: resolved.tracesSampleRate,
    tracePropagationTargets: config.tracePropagationTargets,
    replaysSessionSampleRate: resolved.replaysSessionSampleRate,
    replaysOnErrorSampleRate: resolved.replaysOnErrorSampleRate,
    debug: resolved.debug,
    enableLogs: resolved.enableLogs,
    integrations: getBrowserIntegrations(config) as any,
    beforeSend: (event, hint) => {
      event.extra = sanitize(event.extra);
      event.contexts = sanitize(event.contexts);

      return resolved.beforeSend ? (resolved.beforeSend(event, hint) as typeof event | null) : event;
    }
  });

  return resolved;
}

export function createBrowserLogger(config: BrowserObservabilityConfig = {}): BrowserLogger {
  const resolved = resolveObservabilityConfig(config);

  const write = (level: 'debug' | 'info' | 'warn' | 'error', event: string, payload?: unknown) => {
    const timestamp = new Date().toISOString();
    const entry = sanitize({
      timestamp,
      level,
      event,
      service: resolved.service,
      environment: resolved.environment,
      release: resolved.release,
      payload
    });

    if (level === 'error') {
      const sentryEventId = reportError(payload instanceof Error ? payload : new Error(event), {
        event,
        payload: sanitize(payload)
      } as ErrorContext);
      console.error({ ...entry, sentryEventId });
      return sentryEventId;
    }

    if (resolved.enabled && resolved.dsn) {
      Sentry.addBreadcrumb({
        category: 'log',
        level: level === 'warn' ? 'warning' : level,
        message: event,
        data: sanitize(payload) as Record<string, unknown>
      });
    }

    const consoleMethod = level === 'debug' ? 'debug' : level;
    console[consoleMethod](entry);
    return null;
  };

  return {
    debug: (event, payload) => void write('debug', event, payload),
    info: (event, payload) => void write('info', event, payload),
    warn: (event, payload) => void write('warn', event, payload),
    error: (event, payload) => write('error', event, payload)
  };
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    error: null,
    componentStack: ''
  };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const componentStack = errorInfo.componentStack || '';

    this.setState({ componentStack });
    this.props.onError?.(error, componentStack);
    reportError(error, {
      section: this.props.section,
      componentStack
    });
  }

  resetError = () => {
    this.setState({
      error: null,
      componentStack: ''
    });
  };

  render() {
    const { error, componentStack } = this.state;

    if (!error) {
      return this.props.children;
    }

    const { fallback } = this.props;

    if (typeof fallback === 'function') {
      return fallback({
        error,
        componentStack,
        resetError: this.resetError
      });
    }

    return fallback ?? null;
  }
}

export function useErrorReporter() {
  return React.useCallback((error: unknown, context?: ErrorContext) => reportError(error, context), []);
}

export function createReactRootErrorHandlers() {
  const reactErrorHandler = (Sentry as unknown as { reactErrorHandler?: () => (error: unknown) => void }).reactErrorHandler;

  if (!reactErrorHandler) {
    return {};
  }

  const handler = reactErrorHandler();

  return {
    onUncaughtError: handler,
    onCaughtError: handler,
    onRecoverableError: handler
  };
}

export { Sentry, setObservabilityUser };

function getBrowserIntegrations(config: BrowserObservabilityConfig) {
  const sentryAny = Sentry as unknown as Record<string, (...args: any[]) => unknown>;
  const integrations: unknown[] = [];

  if (sentryAny.browserTracingIntegration) {
    integrations.push(sentryAny.browserTracingIntegration());
  }

  if (sentryAny.replayIntegration) {
    integrations.push(sentryAny.replayIntegration(config.replayOptions));
  }

  if (config.feedback && sentryAny.feedbackIntegration) {
    integrations.push(sentryAny.feedbackIntegration(config.feedbackOptions));
  }

  return integrations;
}
